/**
 * Manual Lead / Booking creation + Lead conversion modals.
 *
 * Used by AdminPortal:
 *  - <CreateLeadModal />        — "+ New Lead" button on Lead Inbox
 *  - <CreateBookingModal />     — "+ New Booking" button on Bookings tab
 *  - <ConvertLeadButton />      — appears inside the Lead drawer when lead.status !== 'converted'
 *
 * Lead sources are fetched once from /admin/lead-sources; preset time slots come from the
 * same endpoint. Both modals submit via the panel `api` axios instance.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { X, Loader2, UserPlus, BookPlus, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const DEFAULT_SOURCES = [
  { key: 'website', label: 'Website' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'phone_call', label: 'Phone Call' },
  { key: 'manual', label: 'Manual' },
  { key: 'walk_in', label: 'Walk-in' },
  { key: 'referral', label: 'Referral' },
  { key: 'social_media', label: 'Social Media' },
  { key: 'chatbot', label: 'Chatbot' },
  { key: 'other', label: 'Other' },
];

const DEFAULT_SLOTS = [
  { key: '08-10', start: '08:00', end: '10:00', label: '8 – 10 AM' },
  { key: '10-12', start: '10:00', end: '12:00', label: '10 AM – 12 PM' },
  { key: '12-14', start: '12:00', end: '14:00', label: '12 – 2 PM' },
  { key: '14-16', start: '14:00', end: '16:00', label: '2 – 4 PM' },
  { key: '16-18', start: '16:00', end: '18:00', label: '4 – 6 PM' },
  { key: '18-20', start: '18:00', end: '20:00', label: '6 – 8 PM' },
];

function useLeadSources(api) {
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  const [presets, setPresets] = useState(DEFAULT_SLOTS);
  useEffect(() => {
    (async () => {
      try {
        const res = await api('GET', '/admin/lead-sources');
        if (res?.data?.sources?.length) setSources(res.data.sources);
        if (res?.data?.presets?.length) setPresets(res.data.presets);
      } catch { /* fall back to defaults */ }
    })();
  }, [api]);
  return { sources, presets };
}

function ModalShell({ title, icon: Icon, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="lb-modal-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
          <div className="flex items-center gap-2">
            {Icon && <Icon size={18} className="text-emerald-700" />}
            <h3 className="font-bold text-slate-900">{title}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="lb-modal-close"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// ============================================================
// CREATE LEAD
// ============================================================
export function CreateLeadModal({ open, api, onClose, onCreated }) {
  const { sources } = useLeadSources(api);
  const [form, setForm] = useState({
    name: '', phone: '', city: '', email: '',
    pest_type: 'general', pest_label: 'General Pest Control',
    property_size: '', estimated_price: '',
    lead_source: 'manual', additional_details: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (!open) return null;

  const submit = async () => {
    setError(null);
    if (!form.name.trim()) return setError('Name is required');
    if (!/^\d{10}$/.test(form.phone)) return setError('Phone must be 10 digits');
    setBusy(true);
    try {
      const payload = {
        ...form,
        estimated_price: form.estimated_price ? Number(form.estimated_price) : null,
        email: form.email || null,
      };
      const res = await api('POST', '/admin/leads-manual', payload);
      onCreated?.(res.data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to create lead');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Create Lead" icon={UserPlus} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={form.name} onChange={update('name')} data-testid="lead-form-name" />
          </div>
          <div>
            <Label className="text-xs">Phone (10-digit) *</Label>
            <Input value={form.phone} maxLength={10} onChange={update('phone')} data-testid="lead-form-phone" />
          </div>
          <div>
            <Label className="text-xs">City</Label>
            <Input value={form.city} onChange={update('city')} data-testid="lead-form-city" />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input value={form.email} onChange={update('email')} type="email" data-testid="lead-form-email" />
          </div>
          <div>
            <Label className="text-xs">Pest / Service</Label>
            <Input value={form.pest_label} onChange={update('pest_label')} placeholder="e.g. Termite Control" data-testid="lead-form-pest" />
          </div>
          <div>
            <Label className="text-xs">Property Size</Label>
            <Input value={form.property_size} onChange={update('property_size')} placeholder="2BHK / 1200 sqft" data-testid="lead-form-property" />
          </div>
          <div>
            <Label className="text-xs">Estimated Price (₹)</Label>
            <Input value={form.estimated_price} type="number" onChange={update('estimated_price')} data-testid="lead-form-price" />
          </div>
          <div>
            <Label className="text-xs">Lead Source *</Label>
            <select
              value={form.lead_source}
              onChange={update('lead_source')}
              className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
              data-testid="lead-form-source"
            >
              {sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Notes / Details</Label>
          <Textarea rows={3} value={form.additional_details} onChange={update('additional_details')} data-testid="lead-form-notes" />
        </div>
        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="lead-form-error">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lead-form-submit">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Creating…</> : 'Create Lead'}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ============================================================
// CREATE BOOKING
// ============================================================
export function CreateBookingModal({ open, api, onClose, onCreated, prefill }) {
  const { sources, presets } = useLeadSources(api);
  const [techs, setTechs] = useState([]);
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '', customer_city: '', customer_address: '',
    service_label: 'General Pest Control', service_slug: '', pest_type: 'general', property_size: '',
    scheduled_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    slot_key: '08-10', custom_start: '', custom_end: '',
    final_amount: '', original_amount: '',
    technician_id: '', admin_notes: '', customer_notes: '',
    lead_source: 'manual',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    (async () => {
      try { const e = await api('GET', '/admin/employees-mini'); setTechs(e.data || []); } catch { /* */ }
    })();
    if (prefill) setForm((f) => ({ ...f, ...prefill }));
  }, [open, api, prefill]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError(null);
    if (!form.customer_name.trim()) return setError('Customer name is required');
    if (!/^\d{10}$/.test(form.customer_phone)) return setError('Phone must be 10 digits');
    if (!form.scheduled_date) return setError('Schedule date is required');
    let start = null, end = null;
    if (form.slot_key === 'custom') {
      if (!form.custom_start || !form.custom_end) return setError('Set custom start and end time');
      start = form.custom_start; end = form.custom_end;
    } else {
      const p = presets.find((x) => x.key === form.slot_key);
      if (p) { start = p.start; end = p.end; }
    }
    setBusy(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email || null,
        customer_city: form.customer_city || null,
        customer_address: form.customer_address || null,
        service_label: form.service_label,
        service_slug: form.service_slug || null,
        pest_type: form.pest_type || 'general',
        property_size: form.property_size || null,
        scheduled_date: form.scheduled_date,
        time_slot_start: start,
        time_slot_end: end,
        final_amount: Number(form.final_amount || 0),
        original_amount: form.original_amount ? Number(form.original_amount) : null,
        technician_id: form.technician_id || null,
        admin_notes: form.admin_notes || null,
        customer_notes: form.customer_notes || null,
        lead_source: form.lead_source,
      };
      const res = await api('POST', '/admin/bookings-manual', payload);
      onCreated?.(res.data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to create booking');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Create Booking" icon={BookPlus} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Customer Name *</Label>
            <Input value={form.customer_name} onChange={update('customer_name')} data-testid="booking-form-name" />
          </div>
          <div>
            <Label className="text-xs">Phone (10-digit) *</Label>
            <Input value={form.customer_phone} maxLength={10} onChange={update('customer_phone')} data-testid="booking-form-phone" />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input value={form.customer_email} type="email" onChange={update('customer_email')} data-testid="booking-form-email" />
          </div>
          <div>
            <Label className="text-xs">City</Label>
            <Input value={form.customer_city} onChange={update('customer_city')} data-testid="booking-form-city" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Address</Label>
            <Input value={form.customer_address} onChange={update('customer_address')} data-testid="booking-form-address" />
          </div>
          <div>
            <Label className="text-xs">Service / Pest *</Label>
            <Input value={form.service_label} onChange={update('service_label')} data-testid="booking-form-service" />
          </div>
          <div>
            <Label className="text-xs">Property Size</Label>
            <Input value={form.property_size} onChange={update('property_size')} placeholder="2BHK / 1200 sqft" data-testid="booking-form-property" />
          </div>
          <div>
            <Label className="text-xs">Schedule Date *</Label>
            <Input type="date" value={form.scheduled_date} onChange={update('scheduled_date')} data-testid="booking-form-date" />
          </div>
          <div>
            <Label className="text-xs">Time Slot *</Label>
            <select
              value={form.slot_key}
              onChange={update('slot_key')}
              className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
              data-testid="booking-form-slot"
            >
              {presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              <option value="custom">Custom…</option>
            </select>
          </div>
          {form.slot_key === 'custom' && (
            <>
              <div>
                <Label className="text-xs">Custom Start (HH:MM)</Label>
                <Input type="time" value={form.custom_start} onChange={update('custom_start')} data-testid="booking-form-start" />
              </div>
              <div>
                <Label className="text-xs">Custom End (HH:MM)</Label>
                <Input type="time" value={form.custom_end} onChange={update('custom_end')} data-testid="booking-form-end" />
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">Amount (₹)</Label>
            <Input type="number" value={form.final_amount} onChange={update('final_amount')} data-testid="booking-form-amount" />
          </div>
          <div>
            <Label className="text-xs">Lead Source *</Label>
            <select
              value={form.lead_source}
              onChange={update('lead_source')}
              className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
              data-testid="booking-form-source"
            >
              {sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Assign Technician</Label>
            <select
              value={form.technician_id}
              onChange={update('technician_id')}
              className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
              data-testid="booking-form-tech"
            >
              <option value="">— Unassigned —</option>
              {techs.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.employee_code || t.mobile})</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Admin Notes</Label>
            <Textarea rows={2} value={form.admin_notes} onChange={update('admin_notes')} data-testid="booking-form-notes" />
          </div>
        </div>
        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="booking-form-error">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="booking-form-submit">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Creating…</> : 'Create Booking'}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ============================================================
// CONVERT LEAD → BOOKING
// ============================================================
export function ConvertLeadModal({ open, api, lead, onClose, onConverted }) {
  const { presets } = useLeadSources(api);
  const [techs, setTechs] = useState([]);
  const [form, setForm] = useState({
    scheduled_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    slot_key: '08-10', custom_start: '', custom_end: '',
    final_amount: lead?.estimated_price || '',
    technician_id: '', admin_notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm((f) => ({ ...f, final_amount: lead?.estimated_price || '' }));
    (async () => {
      try { const e = await api('GET', '/admin/employees-mini'); setTechs(e.data || []); } catch { /* */ }
    })();
  }, [open, lead, api]);

  if (!open || !lead) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError(null);
    if (!form.scheduled_date) return setError('Schedule date is required');
    let start = null, end = null;
    if (form.slot_key === 'custom') {
      if (!form.custom_start || !form.custom_end) return setError('Set custom start and end time');
      start = form.custom_start; end = form.custom_end;
    } else {
      const p = presets.find((x) => x.key === form.slot_key);
      if (p) { start = p.start; end = p.end; }
    }
    setBusy(true);
    try {
      const payload = {
        scheduled_date: form.scheduled_date,
        time_slot_start: start,
        time_slot_end: end,
        final_amount: Number(form.final_amount || 0),
        technician_id: form.technician_id || null,
        admin_notes: form.admin_notes || null,
      };
      const res = await api('POST', `/admin/leads/${lead.id}/convert`, payload);
      onConverted?.(res.data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to convert');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`Convert Lead → Booking · ${lead.name}`} icon={ArrowRight} onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
          <div><span className="text-slate-500">Lead:</span> <span className="font-semibold">{lead.name}</span> · {lead.phone} · {lead.city || '—'}</div>
          <div><span className="text-slate-500">Service:</span> {lead.pest_label || lead.pest_type || 'General'}</div>
          {lead.estimated_price && <div><span className="text-slate-500">Quoted:</span> ₹{Number(lead.estimated_price).toLocaleString('en-IN')}</div>}
          <div><span className="text-slate-500">Source:</span> {lead.lead_source || lead.source || 'website'}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Schedule Date *</Label>
            <Input type="date" value={form.scheduled_date} onChange={update('scheduled_date')} data-testid="convert-form-date" />
          </div>
          <div>
            <Label className="text-xs">Time Slot *</Label>
            <select
              value={form.slot_key}
              onChange={update('slot_key')}
              className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
              data-testid="convert-form-slot"
            >
              {presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              <option value="custom">Custom…</option>
            </select>
          </div>
          {form.slot_key === 'custom' && (
            <>
              <div>
                <Label className="text-xs">Custom Start</Label>
                <Input type="time" value={form.custom_start} onChange={update('custom_start')} />
              </div>
              <div>
                <Label className="text-xs">Custom End</Label>
                <Input type="time" value={form.custom_end} onChange={update('custom_end')} />
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">Final Amount (₹)</Label>
            <Input type="number" value={form.final_amount} onChange={update('final_amount')} data-testid="convert-form-amount" />
          </div>
          <div>
            <Label className="text-xs">Assign Technician</Label>
            <select
              value={form.technician_id}
              onChange={update('technician_id')}
              className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
              data-testid="convert-form-tech"
            >
              <option value="">— Unassigned —</option>
              {techs.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.employee_code || t.mobile})</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Admin Notes</Label>
          <Textarea rows={2} value={form.admin_notes} onChange={update('admin_notes')} data-testid="convert-form-notes" />
        </div>
        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="convert-form-error">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="convert-form-submit">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Converting…</> : 'Convert to Booking'}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ============================================================
// LEAD SOURCE BADGE (shared)
// ============================================================
const LEAD_SOURCE_COLORS = {
  website: 'bg-blue-100 text-blue-700',
  whatsapp: 'bg-emerald-100 text-emerald-700',
  phone_call: 'bg-indigo-100 text-indigo-700',
  manual: 'bg-slate-100 text-slate-700',
  walk_in: 'bg-amber-100 text-amber-700',
  referral: 'bg-purple-100 text-purple-700',
  social_media: 'bg-pink-100 text-pink-700',
  chatbot: 'bg-teal-100 text-teal-700',
  other: 'bg-gray-100 text-gray-700',
};

export function LeadSourceBadge({ source }) {
  if (!source) return null;
  const key = String(source).toLowerCase().replace(/[-\s]/g, '_');
  const cls = LEAD_SOURCE_COLORS[key] || 'bg-gray-100 text-gray-700';
  const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`} data-testid={`lead-source-${key}`}>
      {label}
    </span>
  );
}
